// "While you were out" on Today — on phones, after being away a while.

import { Bone, Cctv, ClipboardCheck, Footprints, GraduationCap, HeartPulse, House, Moon, NotebookPen, Sparkles, TriangleAlert, Utensils } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { dismissAway, useAway } from '../../data/away.ts';
import { useCollection, useDevice, useEvents, useHousehold, useNow, useProfile } from '../../data/hooks.ts';
import { handover, type HandoverKind } from '../../domain/handover.ts';
import { Button, Card } from '../../ui/primitives.tsx';

const ICON: Record<HandoverKind, LucideIcon> = {
  toilet: Sparkles,
  meal: Utensils,
  sleep: Moon,
  walk: Footprints,
  alone: House,
  health: HeartPulse,
  note: NotebookPen,
  camera: Cctv,
  care: ClipboardCheck,
  training: GraduationCap,
  next: Bone,
  flag: TriangleAlert,
};

export function HandoverCard() {
  const away = useAway();
  const d = useDevice();
  const events = useEvents();
  const care = useCollection('care');
  const profile = useProfile();
  const household = useHousehold();
  const visits = useCollection('vetVisits');
  const now = useNow(60_000);
  const h = useMemo(() => (away ? handover({ events, profile, household, care, since: away.since, until: away.until, now, visits }) : undefined), [away, events, profile, household, care, now, visits]);
  // The wall tablet is shared and always on — this is for someone coming back to their phone.
  if (!h || d.kiosk || (!h.logged && !h.next.some((n) => n.alert))) return null;
  return (
    <Card className="handover">
      <div className="handover-head">
        <div>
          <span className="eyebrow">Catch up</span>
          <h2 className="handover-title">{h.title}</h2>
        </div>
        <Button size="sm" variant="primary" onClick={dismissAway}>
          Got it
        </Button>
      </div>
      {h.lines.length ? (
        <ul className="handover-list">
          {h.lines.map((l, i) => {
            const Icon = ICON[l.kind];
            return (
              <li key={i} className={l.alert ? 'alert' : ''}>
                <Icon aria-hidden />
                <span>{l.text}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted">Nothing was logged while you were out.</p>
      )}
      {h.next.length > 0 && (
        <>
          <span className="eyebrow handover-next-label">Next</span>
          <ul className="handover-list next">
            {h.next.map((l, i) => {
              const Icon = ICON[l.kind];
              return (
                <li key={i} className={l.alert ? 'alert' : ''}>
                  <Icon aria-hidden />
                  <span>{l.text}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}
